/*
 * Which reply and forward boxes a conversation has, and which one is open (LEA-514). The thread reads
 * newest first: the reply to the lead is the box at its top, and Reply, Reply all or Forward on any
 * other message opens that message's own box directly above its card. One box is open at a time:
 * opening one folds a reply box that holds words to its bar and closes a forward box, whose words this
 * browser keeps (`forward-draft.ts`). A reply box folded empty goes. The cards ask through the window
 * events `openReply` and `openForward` send, which the top box's mode menu sends too; the state lives
 * outside the component, so a responsive remount or a thread read that leaves a message out keeps it.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import type { InboxMessage, InboxThread } from "../../src/inbox-v2/contract.ts";
import { OPEN_FORWARD_MESSAGE } from "./message-actions.tsx";
import { OPEN_REPLY_MESSAGE } from "./reply-message.tsx";
import { replyAudience } from "./reply-recipient-fields.tsx";
import { HOLD_VIEW_EVENT } from "./use-view-held.ts";

/** The top box's choice: the message and preset a Reply on the lead's own message asked for. */
export type TopSource = { messageId: string; all: boolean; selection: number } | null;
/**
 * A message's box. A reply box's `selection` counts the times it was asked for, so asking again puts
 * the caret back; a box drawn for a saved draft has 0 and waits folded until it is opened.
 */
export type MessageBox =
  | {
      readonly kind: "reply";
      readonly message: InboxMessage;
      readonly all: boolean;
      readonly selection: number;
    }
  | { readonly kind: "forward"; readonly message: InboxMessage };
/** `top`, or a message's box by kind and message id. */
export type BoxKey = string;
export const TOP: BoxKey = "top";
export const keyOf = (box: Pick<MessageBox, "kind" | "message">): BoxKey =>
  `${box.kind}:${box.message.id}`;

type State = { boxes: readonly MessageBox[]; open: BoxKey | null; top: TopSource };
const EMPTY: State = { boxes: [], open: null, top: null };
const kept = new Map<string, State>();

const Detail = z.object({ workspaceId: z.string(), planId: z.string(), messageId: z.string() });
const ReplyDetail = Detail.extend({ all: z.boolean().default(false) });

export type ThreadBoxes = {
  readonly top: TopSource;
  /** A send from the top box put it back on its default, the lead. */
  readonly resetTop: () => void;
  readonly open: BoxKey | null;
  /** A box opened by its own bar, or by R for the top one: every other box folds. */
  readonly opened: (key: BoxKey) => void;
  /** The boxes drawn directly above message `messageId`'s card. */
  readonly above: (messageId: string) => readonly MessageBox[];
  /** Boxes whose message a thread read left out: drawn at the top, so no words vanish with it. */
  readonly orphans: readonly MessageBox[];
  readonly close: (key: BoxKey) => void;
  /** A forward box is open: it draws its own send, so the top box leaves a forward to it. */
  readonly forwarding: boolean;
};

const BoxesContext = createContext<ThreadBoxes | null>(null);
export const ThreadBoxesProvider = BoxesContext.Provider;
export function useBoxes(): ThreadBoxes | null {
  return useContext(BoxesContext);
}

/**
 * Nothing above a box moves as it opens: the item before its card holds its place
 * (`use-view-held.ts`), so the box appears where the card's top was and pushes the card down (the
 * design's C3). Holding the card instead would lift everything above it, the box out of view with it.
 */
function holdAbove(messageId: string): void {
  const item = document.getElementById(`message-${messageId}`)?.closest("li");
  const above = item?.previousElementSibling ?? item?.parentElement?.previousElementSibling;
  above?.dispatchEvent(new CustomEvent(HOLD_VIEW_EVENT, { bubbles: true }));
}

/** Without a forward other than `keep`: a forward closes when another box opens; its words stay. */
const withoutForwards = (boxes: readonly MessageBox[], keep: BoxKey | null) =>
  boxes.filter((box) => box.kind !== "forward" || keyOf(box) === keep);

function upsert(boxes: readonly MessageBox[], box: MessageBox): readonly MessageBox[] {
  const key = keyOf(box);
  return [...withoutForwards(boxes, key).filter((other) => keyOf(other) !== key), box];
}

export function useThreadBoxes(
  userId: string,
  workspaceId: string,
  thread: InboxThread,
): ThreadBoxes {
  const planId = thread.conversation.id;
  const id = [userId, workspaceId, planId].join("\u0000");
  const [state, setState] = useState<State>(() => kept.get(id) ?? EMPTY);
  const update = useCallback(
    (change: (state: State) => State) =>
      setState((current) => {
        const next = change(current);
        kept.set(id, next);
        return next;
      }),
    [id],
  );
  // Another conversation is another set of boxes.
  useEffect(() => setState(kept.get(id) ?? EMPTY), [id]);
  const lead = replyAudience(thread, null).messageId;
  const { messages } = thread;
  // A reply draft saved above a message, here or on another device, is drawn folded with its words.
  const saved = (thread.draftBoxes ?? []).join(" ");
  useEffect(() => {
    if (!saved) return;
    update((current) => {
      const missing = saved
        .split(" ")
        .filter((target) => target !== lead)
        .flatMap((target) => {
          const message = messages.find((candidate) => candidate.id === target);
          return message && !current.boxes.some((box) => keyOf(box) === `reply:${target}`)
            ? [{ kind: "reply" as const, message, all: false, selection: 0 }]
            : [];
        });
      return missing.length ? { ...current, boxes: [...current.boxes, ...missing] } : current;
    });
  }, [saved, lead, messages, update]);
  useEffect(() => {
    const ours = (event: Event) =>
      event instanceof CustomEvent &&
      Detail.safeParse(event.detail).data?.workspaceId === workspaceId &&
      Detail.safeParse(event.detail).data?.planId === planId;
    const reply = (event: Event) => {
      if (!(event instanceof CustomEvent) || !ours(event)) return;
      const asked = ReplyDetail.parse(event.detail);
      const parent = replyAudience(thread, { messageId: asked.messageId, all: asked.all }).parent;
      if (!parent) return;
      if (parent.id === lead) {
        update((current) => ({
          boxes: withoutForwards(current.boxes, null),
          open: TOP,
          top: {
            messageId: asked.messageId,
            all: asked.all,
            selection: (current.top?.selection ?? 0) + 1,
          },
        }));
        return;
      }
      holdAbove(parent.id);
      update((current) => {
        const before = current.boxes.find((box) => keyOf(box) === `reply:${parent.id}`);
        const box = {
          kind: "reply" as const,
          message: parent,
          all: asked.all,
          selection: (before?.kind === "reply" ? before.selection : 0) + 1,
        };
        return { ...current, boxes: upsert(current.boxes, box), open: keyOf(box) };
      });
    };
    const forward = (event: Event) => {
      if (!(event instanceof CustomEvent) || !ours(event)) return;
      const asked = Detail.parse(event.detail);
      const message = thread.messages.find((candidate) => candidate.id === asked.messageId);
      if (!message) return;
      holdAbove(message.id);
      const box = { kind: "forward" as const, message };
      update((current) => ({ ...current, boxes: upsert(current.boxes, box), open: keyOf(box) }));
    };
    window.addEventListener(OPEN_REPLY_MESSAGE, reply);
    window.addEventListener(OPEN_FORWARD_MESSAGE, forward);
    return () => {
      window.removeEventListener(OPEN_REPLY_MESSAGE, reply);
      window.removeEventListener(OPEN_FORWARD_MESSAGE, forward);
    };
  }, [workspaceId, planId, thread, lead, update]);
  return useMemo(() => {
    const shown = new Set(messages.map((message) => message.id));
    return {
      top: state.top,
      resetTop: () => update((current) => ({ ...current, top: null })),
      open: state.open,
      opened: (key) =>
        update((current) =>
          current.open === key
            ? current
            : { ...current, boxes: withoutForwards(current.boxes, key), open: key },
        ),
      above: (messageId) => state.boxes.filter((box) => box.message.id === messageId),
      orphans: state.boxes.filter((box) => !shown.has(box.message.id)),
      close: (key) =>
        update((current) => ({
          ...current,
          boxes: current.boxes.filter((box) => keyOf(box) !== key),
          open: current.open === key ? null : current.open,
        })),
      forwarding: state.boxes.some((box) => box.kind === "forward"),
    };
  }, [state, messages, update]);
}
